import { useState } from "react"

export type TargetDate = Date | string

export type DateDomain = "year" | "month" | "day"

export class DateCore {

  constructor() {
    const todayData = this.getDateData(new Date())
    if (!todayData) throw new Error("DateCore.constructor > invalid initDate")

    this.#todayData = Object.freeze(todayData)
  }

  #todayData
  get todayValues() {
    return {...this.#todayData.dateValues}
  }

  isValidDate(date: unknown) {
    const
    dateIns = date instanceof Date ? date : new Date(String(date)),
    isValid = !Number.isNaN(dateIns.getTime())
    return {
      dateIns,
      isValid
    }
  }

  getDateData(date: TargetDate) {
    const {dateIns, isValid} = this.isValidDate(date)
    if (!isValid) return null

    const
    year = dateIns.getFullYear(),
    month = dateIns.getMonth() + 1,
    day = dateIns.getDate(),
    yearStr = year.toString(),
    monthStr = month.toString().padStart(2, "0"),
    dayStr = day.toString().padStart(2, "0"),
    calendarKey = `${yearStr}-${monthStr}`,
    dateStr = `${calendarKey}-${dayStr}`

    return {
      dateIns,
      dateValues: {
        year, yearStr,
        month, monthStr,
        day, dayStr,
        calendarKey, dateStr,
      }
    } 
  }
  
  isDateDomain(domain: string): domain is DateDomain {
    return ["year", "month", "day"].includes(domain)
  }

}

export class CalendarCore extends DateCore {

  getYearCalendarData(year: number | string, length = 12) {
    const
    y = Math.abs(Number.parseInt(String(year))),
    n = Number.parseInt(String(length)),
    gap = Math.floor((y - 1) / n)
    return {
      domain: "year",
      list: Array.from({length: n}, (_, i) => {
        const year = (i + 1) + n * gap

        return {
          year,
          yearStr: year.toString()
        }
      })
    }
  }

  getMonthCalendarData(year: number | string) {
    const
    _year = Math.abs(Number.parseInt(String(year)))
    
    return {
      domain: "month",
      list: Array.from({length: 12}, (_, i) => {
        const
        month = i + 1,
        monthStr = month.toString().padStart(2, "0"),
        calendarKey = `${_year}-${monthStr}`

        return {
          year: _year,
          yearStr: _year.toString(),
          month,
          monthStr,
          calendarKey,
        }
      })
    }
  }

  getDayCalendarData(date: TargetDate) {
    const data = super.getDateData(date)
    if (!data) return null
    
    const
    {
      dateIns,
      dateValues
    } = data,
    {
      year, yearStr,
      month, monthStr,
      day, dayStr,
      calendarKey, dateStr,
    } = dateValues,
    dayIndex = dateIns.getDay(),
    // year/month
    isJanuary = month == 1,
    prevYear = isJanuary ? (year - 1) : year,
    prevMonth = isJanuary ? 12 : (month - 1),
    isDecember = month == 12,
    nextYear = isDecember ? (year + 1) : year,
    nextMonth = isDecember ? 1 : (month + 1)
    // day > prev
    dateIns.setFullYear(prevYear, prevMonth, 0)
    const prevMonthLastDay = dateIns.getDate()
    const prevMonthLastDayIndex = dateIns.getDay()
    dateIns.setDate(1)
    const prevMonthFirstDayIndex = dateIns.getDay()
    // day > curr
    dateIns.setFullYear(year, month, 0)
    const lastDay = dateIns.getDate()
    const lastDayIndex = dateIns.getDay()
    const firstDayIndex = (prevMonthLastDayIndex == 6) ? 0 : (prevMonthLastDayIndex + 1)
    dateIns.setFullYear(nextYear, nextMonth, 0)
    const nextMonthLastDay = dateIns.getDate()
    const nextMonthLastDayIndex = dateIns.getDay()
    const nextMonthFirstDayIndex = (lastDayIndex == 6) ? 0 : (lastDayIndex + 1),
    // key
    prevYearStr = prevYear.toString(),
    prevMonthStr = prevMonth.toString().padStart(2, "0"),
    prevCalendarKey = `${prevYearStr}-${prevMonthStr}`,
    nextYearStr = nextYear.toString(),
    nextMonthStr = nextMonth.toString().padStart(2, "0"),
    nextCalendarKey = `${nextYearStr}-${nextMonthStr}`,
    // result
    prev = {
      year: prevYear,
      month: prevMonth,
      yearStr: prevYearStr,
      monthStr: prevMonthStr,
      calendarKey: prevCalendarKey,
      firstDayIndex: prevMonthFirstDayIndex,
      lastDay: prevMonthLastDay,
      lastDayIndex: prevMonthLastDayIndex,
    },
    curr = {
      year,
      month,
      day,
      yearStr,
      monthStr,
      dayStr,
      dateStr,
      calendarKey,
      dayIndex,
      firstDayIndex,
      lastDayIndex,
      lastDay,
      isJanuary,
      isDecember,
    },
    next = {
      year: nextYear,
      month: nextMonth,
      yearStr: nextYearStr,
      monthStr: nextMonthStr,
      calendarKey: nextCalendarKey,
      firstDayIndex: nextMonthFirstDayIndex,
      lastDay: nextMonthLastDay,
      lastDayIndex: nextMonthLastDayIndex,
    },
    minLength = 7 * 6,
    list: {
      domain: "prev" | "curr" | "next",
      year: number,
      month: number,
      day: number,
      dayStr: string,
      dayIndex: number,
      calendarKey: string,
      dateStr: string,
    }[] = []
    // fill list
    for (let i = 0; i < curr.firstDayIndex; i++) {
      const
      day = prev.lastDay - i,
      dayStr = day.toString().padStart(2, "0")
      list.unshift({
        domain: "prev",
        year: prev.year,
        month: prev.month,
        day,
        dayStr,
        dayIndex: curr.firstDayIndex - 1 - i,
        calendarKey: prev.calendarKey,
        dateStr: `${prev.calendarKey}-${dayStr}`,
      })
    }
    for (let i = 0; i < curr.lastDay; i++) {
      const
      day = i + 1,
      dayStr = day.toString().padStart(2, "0")
      list.push({
        domain: "curr",
        year: curr.year,
        month: curr.month,
        day,
        dayStr,
        dayIndex: list.length % 7,
        calendarKey: curr.calendarKey,
        dateStr: `${curr.calendarKey}-${dayStr}`,
      })
    }
    for (let i = 0; list.length < minLength; i++) {
      const
      day = i + 1,
      dayStr = day.toString().padStart(2, "0"),
      dateStr = `${next.calendarKey}-${dayStr}`
      list.push({
        domain: "next",
        day,
        dayStr,
        dayIndex: list.length % 7,
        dateStr,
        calendarKey: next.calendarKey,
        year: next.year,
        month: next.month,
      })
    }
    // dateIns reset
    dateIns.setFullYear(year, month - 1, day)

    return {
      domain: "day",
      dateIns,
      prev,
      curr,
      next,
      list
    }
  }

}

export type DateData = Exclude<ReturnType<typeof DateCore.prototype.getDateData>, null>

export type DateValues = DateData["dateValues"]

export type YearCalendarData = Exclude<ReturnType<typeof CalendarCore.prototype.getYearCalendarData>, null>

export type MonthCalendarData = Exclude<ReturnType<typeof CalendarCore.prototype.getMonthCalendarData>, null>

export type DayCalendarData = Exclude<ReturnType<typeof CalendarCore.prototype.getDayCalendarData>, null>

export const useTodayState = () => useState(() => {
  const dateIns = new Date()
  const year = dateIns.getFullYear()
  const monthStr = (dateIns.getMonth() + 1).toString().padStart(2, "0")
  const dayStr = dateIns.getDate().toString().padStart(2, "0")
  return `${year}-${monthStr}-${dayStr}`
})